class ColorSiblingsSwatches extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  get #toggleButton() {
    return this.querySelector('.color-siblings-swatches__overflow-toggle');
  }

  #openedViaKeyboard = false;

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    const toggleButton = this.#toggleButton;

    if (!toggleButton) return;

    this.#syncA11yState(this.#isOverflowOpen());
    this.#syncOverflowItemsA11y(this.#isOverflowOpen());

    toggleButton.addEventListener('keydown', this.#handleToggleKeyDown, { signal });
    toggleButton.addEventListener('pointerdown', this.#handleTogglePointerDown, { signal });
    toggleButton.addEventListener('click', this.#handleToggleClick, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * Marks the next toggle as keyboard-initiated when Enter or Space is pressed.
   * @param {KeyboardEvent} event
   */
  #handleToggleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      this.#openedViaKeyboard = true;
    }
  };

  /**
   * Clears the keyboard flag when the pointer is used so mouse clicks
   * do not trigger focus movement.
   */
  #handleTogglePointerDown = () => {
    this.#openedViaKeyboard = false;
  };

  /**
   * Returns whether the overflow list is expanded.
   * @returns {boolean}
   */
  #isOverflowOpen() {
    return this.classList.contains('is-overflow-open');
  }

  /**
   * Keeps aria-expanded and aria-label in sync with open/closed state.
   * @param {boolean} isOpen
   */
  #syncA11yState(isOpen) {
    const toggleButton = this.#toggleButton;
    if (!toggleButton) return;

    toggleButton.setAttribute('aria-expanded', String(isOpen));

    const label = isOpen ? toggleButton.dataset.labelLess : toggleButton.dataset.labelMore;
    if (label) toggleButton.setAttribute('aria-label', label);
  }

  /**
   * Keeps overflow items out of tab order while collapsed.
   * @param {boolean} isOpen
   */
  #syncOverflowItemsA11y(isOpen) {
    const visibleLimit = parseInt(this.dataset.visibleLimit ?? '5', 10);
    const swatches = this.querySelectorAll('.color-siblings-swatches__item');

    swatches.forEach((swatch, index) => {
      if (!(swatch instanceof HTMLElement)) return;
      const isOverflowItem = index >= visibleLimit - 1;
      if (!isOverflowItem) return;

      const hidden = !isOpen;
      swatch.toggleAttribute('aria-hidden', hidden);
      hidden ? swatch.setAttribute('tabindex', '-1') : swatch.removeAttribute('tabindex');
    });
  }

  /**
   * Toggles overflow visibility and moves focus to the first revealed swatch
   * after keyboard-initiated expansion.
   */
  #handleToggleClick = () => {
    const nextOpenState = !this.#isOverflowOpen();
    this.classList.toggle('is-overflow-open', nextOpenState);
    this.#syncA11yState(nextOpenState);
    this.#syncOverflowItemsA11y(nextOpenState);

    if (!nextOpenState || !this.#openedViaKeyboard) {
      this.#openedViaKeyboard = false;
      return;
    }

    this.#openedViaKeyboard = false;

    const visibleLimit = parseInt(this.dataset.visibleLimit ?? '5', 10);
    const firstHiddenSwatch = /** @type {HTMLElement | undefined} */ (
      this.querySelectorAll('.color-siblings-swatches__item')[visibleLimit - 1]
    );

    firstHiddenSwatch?.focus();
  };
}

if (!customElements.get('color-siblings-swatches')) {
  customElements.define('color-siblings-swatches', ColorSiblingsSwatches);
}
